import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Progress,
  Select,
  Space,
  Table,
  Tabs,
  Upload,
} from "antd";
import axios from "axios";
import * as tus from "tus-js-client";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CopyOutlined, UploadOutlined } from "@ant-design/icons";

const { TabPane } = Tabs;

const BunnyStreamSetup = () => {
  const admin = JSON.parse(localStorage.getItem("ADMIN"));
  const [createCollectionLoading, setCreateCollectionLoading] = useState(false);
  const [listCollectionLoading, setListCollectionLoading] = useState(false);
  const [updateCollectionLoading, setUpdateCollectionLoading] = useState(false);
  const [createVideoLoading, setCreateVideoLoading] = useState(false);
  const [videoStatusLoading, setVideoStatusLoading] = useState(false);
  const [videoUrlsLoading, setVideoUrlsLoading] = useState(false);
  const [listVideosLoading, setListVideosLoading] = useState(false);
  const [streamUploadLoading, setStreamUploadLoading] = useState(false);
  const [storageFilesLoading, setStorageFilesLoading] = useState(false);

  const [collections, setCollections] = useState([]);
  const [totalCollections, setTotalCollections] = useState(0);
  const [videos, setVideos] = useState([]);
  const [totalVideos, setTotalVideos] = useState(0);
  const [videoStatusData, setVideoStatusData] = useState(null);
  const [videoUrlsData, setVideoUrlsData] = useState(null);
  const [storageFiles, setStorageFiles] = useState([]);
  const [uploadedVideoLink, setUploadedVideoLink] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [selectedStreamFile, setSelectedStreamFile] = useState(null);
  const [editingCollection, setEditingCollection] = useState(null);
  const [addVideoCollection, setAddVideoCollection] = useState(null);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("create-collection");

  const [createCollectionForm] = Form.useForm();
  const [listCollectionForm] = Form.useForm();
  const [updateCollectionForm] = Form.useForm();
  const [createVideoForm] = Form.useForm();
  const [videoStatusForm] = Form.useForm();
  const [videoUrlsForm] = Form.useForm();
  const [listVideosForm] = Form.useForm();
  const [streamUploadForm] = Form.useForm();
  const [storageFilesForm] = Form.useForm();

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const tokenHeaders = {
    "x-access-token": admin?.token,
  };
  const tokenAndJsonHeaders = {
    ...tokenHeaders,
    "Content-Type": "application/json",
  };

  const createCollection = async (values) => {
    try {
      setCreateCollectionLoading(true);
      await axios.post(
        `${baseUrl}/api/bunny/collections`,
        { name: values.name },
        {
          headers: tokenAndJsonHeaders,
        }
      );
      message.success("Collection created successfully");
      createCollectionForm.resetFields();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to create collection");
    } finally {
      setCreateCollectionLoading(false);
    }
  };

  const listCollections = async (values) => {
    const page = values?.page || 1;
    const limit = values?.limit || 100;

    try {
      setListCollectionLoading(true);
      const response = await axios.get(
        `${baseUrl}/api/bunny/collections?page=${page}&limit=${limit}`,
        {
          headers: tokenHeaders,
        }
      );

      const list =
        response?.data?.data?.items ||
        response?.data?.data ||
        response?.data?.collections ||
        [];
      const total =
        response?.data?.data?.totalItems ||
        response?.data?.total ||
        (Array.isArray(list) ? list.length : 0);

      setCollections(Array.isArray(list) ? list : []);
      setTotalCollections(total);
      message.success("Collections fetched successfully");
      return Array.isArray(list) ? list : [];
    } catch (error) {
      setCollections([]);
      setTotalCollections(0);
      message.error(error?.response?.data?.message || "Failed to fetch collections");
      return [];
    } finally {
      setListCollectionLoading(false);
    }
  };

  const updateCollection = async (values) => {
    try {
      setUpdateCollectionLoading(true);
      await axios.post(
        `${baseUrl}/api/bunny/collections/${values.collectionId}`,
        { name: values.name },
        {
          headers: tokenAndJsonHeaders,
        }
      );
      message.success("Collection updated successfully");
      updateCollectionForm.resetFields();
      setEditingCollection(null);
      const currentListFilters = listCollectionForm.getFieldsValue();
      await listCollections(currentListFilters);
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to update collection");
    } finally {
      setUpdateCollectionLoading(false);
    }
  };

  const createVideoAndUpload = async (values) => {
    if (!selectedVideoFile) {
      message.error("Please choose a video file first");
      return;
    }

    try {
      setCreateVideoLoading(true);
      setUploadedVideoLink("");
      setUploadProgress(0);

      const createResponse = await axios.post(
        `${baseUrl}/api/bunny/create-video`,
        {
          title: values.title,
          collectionId: values.collectionId || addVideoCollection?._id || addVideoCollection?.id,
          filetype: values.filetype || selectedVideoFile.type || "video/mp4",
        },
        {
          headers: tokenAndJsonHeaders,
        }
      );

      const payload = createResponse?.data?.data || createResponse?.data || {};
      const tusEndpoint = payload?.tusEndpoint;
      const bunnyTusHeaders = payload?.headers || {};

      if (!tusEndpoint) {
        throw new Error("TUS endpoint missing in create-video response");
      }

      await new Promise((resolve, reject) => {
        const upload = new tus.Upload(selectedVideoFile, {
          endpoint: tusEndpoint,
          metadata: {
            filename: selectedVideoFile.name,
            filetype:
              payload?.uploadMetadata?.filetype ||
              selectedVideoFile.type ||
              values.filetype ||
              "video/mp4",
            title: payload?.uploadMetadata?.title || values.title,
          },
          headers: {
            ...bunnyTusHeaders,
          },
          retryDelays: [0, 3000, 5000, 10000],
          uploadDataDuringCreation: true,
          removeFingerprintOnSuccess: true,
          onError: (err) => reject(err),
          onProgress: (bytesUploaded, bytesTotal) => {
            const percentage = bytesTotal ? Math.floor((bytesUploaded / bytesTotal) * 100) : 0;
            setUploadProgress(percentage);
          },
          onSuccess: () => resolve(),
        });
        upload.start();
      });

      const linkFromResponse =
        payload?.videoUrl || payload?.playbackUrl || payload?.cdnUrl || payload?.streamUrl || "";
      setUploadedVideoLink(linkFromResponse);
      message.success("Video created and uploaded successfully");
      createVideoForm.resetFields();
      setSelectedVideoFile(null);
      setAddVideoCollection(null);
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to upload video");
    } finally {
      setCreateVideoLoading(false);
    }
  };

  const getVideoStatus = async (values) => {
    try {
      setVideoStatusLoading(true);
      const response = await axios.get(`${baseUrl}/api/bunny/video-status/${values.videoId}`, {
        headers: tokenHeaders,
      });
      setVideoStatusData(response?.data?.data || response?.data || null);
      message.success("Video status fetched successfully");
    } catch (error) {
      setVideoStatusData(null);
      message.error(error?.response?.data?.message || "Failed to fetch video status");
    } finally {
      setVideoStatusLoading(false);
    }
  };

  const getVideoUrls = async (values) => {
    try {
      setVideoUrlsLoading(true);
      const response = await axios.get(`${baseUrl}/api/bunny/video-hls/${values.videoId}`, {
        headers: tokenHeaders,
      });
      setVideoUrlsData(response?.data?.data || response?.data || null);
      message.success("Video URLs fetched successfully");
    } catch (error) {
      setVideoUrlsData(null);
      message.error(error?.response?.data?.message || "Failed to fetch video URLs");
    } finally {
      setVideoUrlsLoading(false);
    }
  };

  const listVideos = async (values) => {
    const page = values?.page || 1;
    const limit = values?.limit || 20;
    const collectionId = values?.collectionId || "";
    const queryParams = [`page=${page}`, `limit=${limit}`];
    if (collectionId) {
      queryParams.push(`collectionId=${collectionId}`);
    }

    try {
      setListVideosLoading(true);
      const response = await axios.get(
        `${baseUrl}/api/bunny/videos?${queryParams.join("&")}`,
        {
          headers: tokenHeaders,
        }
      );

      const list =
        response?.data?.data?.items ||
        response?.data?.data ||
        response?.data?.videos ||
        [];
      const total =
        response?.data?.data?.totalItems ||
        response?.data?.total ||
        (Array.isArray(list) ? list.length : 0);

      const normalizedVideos = Array.isArray(list) ? list : [];
      setVideos(normalizedVideos);
      setTotalVideos(total);
      message.success("Videos fetched successfully");
      return normalizedVideos;
    } catch (error) {
      setVideos([]);
      setTotalVideos(0);
      message.error(error?.response?.data?.message || "Failed to fetch videos");
      return [];
    } finally {
      setListVideosLoading(false);
    }
  };

  const uploadVideoStream = async (values) => {
    if (!selectedStreamFile) {
      message.error("Please choose a file first");
      return;
    }

    try {
      setStreamUploadLoading(true);
      const fileNameWithExtension = selectedStreamFile.name;

      const response = await axios.post(`${baseUrl}/api/bunny/upload-video-stream`, selectedStreamFile, {
        headers: {
          "x-access-token": admin?.token,
          "x-file-name": fileNameWithExtension,
          "x-folder-path": values?.folderPath || "uploads",
          "Content-Type": "application/octet-stream",
        },
      });

      message.success(response?.data?.message || "File uploaded successfully");
      streamUploadForm.resetFields();
      setSelectedStreamFile(null);
      setIsUploadModalVisible(false);
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to upload stream file");
    } finally {
      setStreamUploadLoading(false);
    }
  };

  const resetUploadModal = () => {
    setSelectedStreamFile(null);
    streamUploadForm.resetFields();
  };

  const closeUploadModal = () => {
    setIsUploadModalVisible(false);
    resetUploadModal();
  };

  const resetEditCollectionModal = () => {
    updateCollectionForm.resetFields();
  };

  const closeEditCollectionModal = () => {
    setEditingCollection(null);
    resetEditCollectionModal();
  };

  const resetAddVideoModal = () => {
    setSelectedVideoFile(null);
    setUploadedVideoLink("");
    setUploadProgress(0);
    createVideoForm.resetFields();
  };

  const closeAddVideoModal = () => {
    setAddVideoCollection(null);
    resetAddVideoModal();
  };

  const getVideoIdentifier = (record) =>
    record?.Guid || record?.guid || record?.videoId || record?.bunnyGuid || record?._id || record?.id || "";

  const listStorageFiles = async (selectedPath) => {
    const path = selectedPath || storageFilesForm.getFieldValue("path") || "uploads";

    try {
      setStorageFilesLoading(true);
      const response = await axios.get(`${baseUrl}/api/bunny/storage-files?path=${encodeURIComponent(path)}`, {
        headers: tokenHeaders,
      });
      const list = response?.data?.data || response?.data?.files || response?.data || [];
      setStorageFiles(Array.isArray(list) ? list : []);
      message.success("Storage files fetched successfully");
    } catch (error) {
      setStorageFiles([]);
      message.error(error?.response?.data?.message || "Failed to fetch storage files");
    } finally {
      setStorageFilesLoading(false);
    }
  };

  useEffect(() => {
    const autoFetchByTab = async () => {
    if (activeTab === "list-collections") {
      const values = listCollectionForm.getFieldsValue();
      await listCollections({
        page: values?.page || 1,
        limit: values?.limit || 100,
      });
    }

    if (activeTab === "list-videos") {
      const values = listVideosForm.getFieldsValue();
      let availableCollections = collections;
      if (!availableCollections?.length) {
        availableCollections = await listCollections({ page: 1, limit: 100 });
      }

      const selectedCollectionId =
        values?.collectionId ||
        availableCollections?.[0]?.bunnyGuid ||
        availableCollections?.[0]?._id ||
        availableCollections?.[0]?.id;

      if (selectedCollectionId) {
        listVideosForm.setFieldsValue({ collectionId: selectedCollectionId });
        await listVideos({
          page: values?.page || 1,
          limit: values?.limit || 20,
          collectionId: selectedCollectionId,
        });
      }
    }

    if (activeTab === "video-status") {
      const currentVideos = await listVideos({
        page: 1,
        limit: 100,
        collectionId: "",
      });
      const firstVideoId = getVideoIdentifier((currentVideos || [])[0]);
      if (firstVideoId) {
        videoStatusForm.setFieldsValue({ videoId: firstVideoId });
      }
    }

    if (activeTab === "video-urls") {
      const currentVideos = await listVideos({
        page: 1,
        limit: 100,
        collectionId: "",
      });
      const firstVideoId = getVideoIdentifier((currentVideos || [])[0]);
      if (firstVideoId) {
        videoUrlsForm.setFieldsValue({ videoId: firstVideoId });
      }
    }

    if (activeTab === "storage-files") {
      await listStorageFiles();
    }
    };

    autoFetchByTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const collectionColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (value) => value || "-",
    },
    {
      title: "Collection Id",
      dataIndex: "_id",
      key: "_id",
      render: (value, record) => value || record?.id || "-",
    },
    {
      title: "Bunny Guid",
      dataIndex: "bunnyGuid",
      key: "bunnyGuid",
      render: (value) => value || "-",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            size="small"
            onClick={() => {
              setEditingCollection(record);
              updateCollectionForm.setFieldsValue({
                collectionId: record?.bunnyGuid || record?._id || record?.id,
                name: record?.name,
              });
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={() => {
              setAddVideoCollection(record);
              createVideoForm.setFieldsValue({
                collectionId: record?.bunnyGuid || record?._id || record?.id,
                filetype: "video/mp4",
              });
            }}
          >
            Add Video
          </Button>
        </div>
      ),
    },
  ];

  const videoColumns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (value) => value || "-",
    },
    {
      title: "Video Id",
      dataIndex: "_id",
      key: "_id",
      render: (value, record) => value || getVideoIdentifier(record) || "-",
    },
    {
      title: "Collection Id",
      dataIndex: "collectionId",
      key: "collectionId",
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => value || "-",
    },
  ];

  const STORAGE_CDN_BASE = "https://clap-store.b-cdn.net/uploads/";

  const getStorageFileName = (record) =>
    record?.name || record?.fileName || record?.ObjectName || "";

  const getStorageFileUrl = (record) => {
    const fileName = getStorageFileName(record);
    if (!fileName) return "";
    return `${STORAGE_CDN_BASE}${fileName}`;
  };

  const storageFileColumns = [
    {
      title: "File Name",
      dataIndex: "name",
      key: "name",
      render: (_, record) => {
        const fileName = getStorageFileName(record);
        if (!fileName) return "-";
        const fileUrl = getStorageFileUrl(record);
        return (
          <Space size={8} align="center">
            <a href={fileUrl} target="_blank" rel="noreferrer">
              {fileUrl}
            </a>
            <CopyToClipboard
              text={fileUrl}
              onCopy={() => message.success("Link copied to clipboard")}
            >
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                aria-label="Copy link"
              />
            </CopyToClipboard>
          </Space>
        );
      },
    },
    {
      title: "Path",
      dataIndex: "path",
      key: "path",
      render: (value, record) => value || record?.Path || "-",
    },
    {
      title: "Last Changed",
      dataIndex: "LastChanged",
      key: "LastChanged",
      render: (value, record) => {
        const dateValue = value || record?.LastChanged;
        return dateValue ? new Date(dateValue).toLocaleString() : "-";
      },
    },
  ];

  const videoStatusColumns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (value) => value || "-",
    },
    {
      title: "Date Uploaded",
      dataIndex: "dateUploaded",
      key: "dateUploaded",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "Views",
      dataIndex: "views",
      key: "views",
      render: (value) => (value || value === 0 ? value : "-"),
    },
    {
      title: "Average Watch Time",
      dataIndex: "averageWatchTime",
      key: "averageWatchTime",
      render: (value) => (value || value === 0 ? value : "-"),
    },
    {
      title: "Total Watch Time",
      dataIndex: "totalWatchTime",
      key: "totalWatchTime",
      render: (value) => (value || value === 0 ? value : "-"),
    },
  ];

  const videoUrlsColumns = [
    {
      title: "HLS URL",
      dataIndex: "hlsUrl",
      key: "hlsUrl",
      render: (value) =>
        value ? (
          <a href={value} target="_blank" rel="noreferrer">
            {value}
          </a>
        ) : (
          "-"
        ),
    },
  ];

  const videoIdOptions = Array.from(
    new Map(
      videos
        .map((video) => {
          const id = getVideoIdentifier(video);
          const title = video?.title || "Untitled";
          if (!id) return null;
          return [
            id,
            {
              value: id,
              label: `${title} (${id})`,
            },
          ];
        })
        .filter(Boolean)
    ).values()
  );

  return (
    <Card title="Bunny Stream Setup">
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        defaultActiveKey="create-collection"
      >
        <TabPane tab="Create Collection" key="create-collection">
          <Form
            form={createCollectionForm}
            layout="vertical"
            onFinish={createCollection}
            style={{ maxWidth: 500 }}
          >
            <Form.Item
              label="Collection Name"
              name="name"
              rules={[{ required: true, message: "Please enter collection name" }]}
            >
              <Input placeholder="set 1" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={createCollectionLoading}>
                Create Collection
              </Button>
            </Form.Item>
          </Form>
        </TabPane>

        <TabPane tab="List Collections" key="list-collections">
          <Form
            form={listCollectionForm}
            layout="inline"
            initialValues={{ page: 1, limit: 100 }}
            onFinish={listCollections}
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              label="Page"
              name="page"
              rules={[{ required: true, message: "Page is required" }]}
            >
              <InputNumber min={1} />
            </Form.Item>
            <Form.Item
              label="Limit"
              name="limit"
              rules={[{ required: true, message: "Limit is required" }]}
            >
              <InputNumber min={1} max={500} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={listCollectionLoading}>
                Fetch Collections
              </Button>
            </Form.Item>
          </Form>

          <Table
            loading={listCollectionLoading}
            columns={collectionColumns}
            dataSource={collections}
            rowKey={(record) => record?.bunnyGuid || record?._id || record?.id || record?.name}
            pagination={false}
          />
          <div style={{ marginTop: 12 }}>Total Collections: {totalCollections}</div>
        </TabPane>

        <TabPane tab="Video Status" key="video-status">
          <Form
            form={videoStatusForm}
            layout="inline"
            onFinish={getVideoStatus}
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              label="Video Id"
              name="videoId"
              rules={[{ required: true, message: "Please select video id" }]}
            >
              <Select
                placeholder="Select video id"
                style={{ width: 440 }}
                options={videoIdOptions}
                optionFilterProp="label"
                showSearch
                dropdownStyle={{ zIndex: 1400 }}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={videoStatusLoading}>
                Get Status
              </Button>
            </Form.Item>
          </Form>
          <Table
            columns={videoStatusColumns}
            dataSource={videoStatusData ? [videoStatusData] : []}
            rowKey={(record) => record?.Guid || record?.guid || record?.videoId || record?._id || record?.id}
            pagination={false}
            locale={{ emptyText: "No status loaded" }}
          />
        </TabPane>

        <TabPane tab="Video URLs" key="video-urls">
          <Form
            form={videoUrlsForm}
            layout="inline"
            onFinish={getVideoUrls}
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              label="Video Id"
              name="videoId"
              rules={[{ required: true, message: "Please select video id" }]}
            >
              <Select
                placeholder="Select video id"
                style={{ width: 440 }}
                options={videoIdOptions}
                optionFilterProp="label"
                showSearch
                dropdownStyle={{ zIndex: 1400 }}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={videoUrlsLoading}>
                Get URLs
              </Button>
            </Form.Item>
          </Form>
          <Table
            columns={videoUrlsColumns}
            dataSource={videoUrlsData ? [videoUrlsData] : []}
            rowKey={(record) => record?.hlsUrl || record?.iframeUrl || record?.thumbnailUrl || "video-urls"}
            pagination={false}
            locale={{ emptyText: "No URLs loaded" }}
          />
        </TabPane>

        <TabPane tab="List Videos" key="list-videos">
          <Form
            form={listVideosForm}
            layout="inline"
            initialValues={{ page: 1, limit: 20 }}
            onFinish={listVideos}
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              label="Collection Id"
              name="collectionId"
              rules={[{ required: true, message: "Please select a collection" }]}
            >
              <Select
                placeholder="Select collection"
                style={{ width: 260 }}
                onChange={(value) => {
                  const currentValues = listVideosForm.getFieldsValue();
                  listVideos({
                    page: currentValues?.page || 1,
                    limit: currentValues?.limit || 20,
                    collectionId: value,
                  });
                }}
              >
                {collections.map((collection) => {
                  const id = collection?.bunnyGuid || collection?._id || collection?.id;
                  return (
                    <Select.Option key={id} value={id}>
                      {collection?.name || id}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Form.Item label="Page" name="page">
              <InputNumber min={1} />
            </Form.Item>
            <Form.Item label="Limit" name="limit">
              <InputNumber min={1} max={100} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={listVideosLoading}>
                Fetch Videos
              </Button>
            </Form.Item>
          </Form>
          <Table
            loading={listVideosLoading}
            columns={videoColumns}
            dataSource={videos}
            rowKey={(record) => getVideoIdentifier(record) || record?.title}
            pagination={false}
          />
          <div style={{ marginTop: 12 }}>Total Videos: {totalVideos}</div>
        </TabPane>

        <TabPane tab="Storage Files" key="storage-files">
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              onClick={() => setIsUploadModalVisible(true)}
              style={{ marginRight: 8 }}
            >
              Upload
            </Button>
            <Button type="primary" onClick={() => listStorageFiles()} loading={storageFilesLoading}>
              Refresh Files
            </Button>
          </div>
          <Form
            form={storageFilesForm}
            layout="inline"
            initialValues={{ path: "uploads" }}
            style={{ marginBottom: 16 }}
          >
            <Form.Item label="Path" name="path">
              <Select style={{ width: 180 }} onChange={(value) => listStorageFiles(value)}>
                <Select.Option value="uploads">uploads</Select.Option>
                <Select.Option value="thumbnails">thumbnails</Select.Option>
              </Select>
            </Form.Item>
          </Form>
          <Table
            loading={storageFilesLoading}
            columns={storageFileColumns}
            dataSource={storageFiles}
            rowKey={(record) =>
              record?.Guid || record?.guid || record?.path || record?.Path || record?.name || record?.ObjectName
            }
            pagination={false}
          />
        </TabPane>
      </Tabs>

      <Modal
        visible={isUploadModalVisible}
        title="Upload File"
        onCancel={closeUploadModal}
        afterClose={resetUploadModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={streamUploadForm}
          layout="vertical"
          onFinish={uploadVideoStream}
          initialValues={{ folderPath: "uploads" }}
        >
          <Form.Item
            label="Folder Path"
            name="folderPath"
          >
            <Input placeholder="uploads" />
          </Form.Item>
          <Form.Item label="File" required>
            <Upload
              beforeUpload={(file) => {
                setSelectedStreamFile(file);
                return false;
              }}
              maxCount={1}
              fileList={selectedStreamFile ? [selectedStreamFile] : []}
              onRemove={() => setSelectedStreamFile(null)}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={streamUploadLoading}>
              Upload Stream File
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        visible={Boolean(editingCollection)}
        title="Edit Collection"
        onCancel={closeEditCollectionModal}
        afterClose={resetEditCollectionModal}
        footer={null}
        destroyOnClose
      >
        <Form form={updateCollectionForm} layout="vertical" onFinish={updateCollection}>
          <Form.Item
            label="Collection Id"
            name="collectionId"
            rules={[{ required: true, message: "Please enter collection id" }]}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            label="Collection Name"
            name="name"
            rules={[{ required: true, message: "Please enter updated collection name" }]}
          >
            <Input placeholder="SET_1" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={updateCollectionLoading}>
              Update Collection
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        visible={Boolean(addVideoCollection)}
        title="Add Video To Collection"
        onCancel={closeAddVideoModal}
        afterClose={resetAddVideoModal}
        footer={null}
        destroyOnClose
      >
        <Form form={createVideoForm} layout="vertical" onFinish={createVideoAndUpload}>
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Please enter video title" }]}
          >
            <Input placeholder="test 1" />
          </Form.Item>
          <Form.Item
            label="Collection Id"
            name="collectionId"
            rules={[{ required: true, message: "Please enter collection id" }]}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item label="File Type" name="filetype" initialValue="video/mp4">
            <Input placeholder="video/mp4" />
          </Form.Item>
          <Form.Item label="Video File" required>
            <Upload
              beforeUpload={(file) => {
                setSelectedVideoFile(file);
                return false;
              }}
              maxCount={1}
              fileList={selectedVideoFile ? [selectedVideoFile] : []}
              onRemove={() => setSelectedVideoFile(null)}
            >
              <Button icon={<UploadOutlined />}>Select Video</Button>
            </Upload>
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Button type="primary" htmlType="submit" loading={createVideoLoading}>
              Create Video and Upload
            </Button>
          </Form.Item>
        </Form>
        <Progress percent={uploadProgress} />
        {uploadedVideoLink ? (
          <div style={{ marginTop: 12 }}>
            Video Link:
            <a href={uploadedVideoLink} target="_blank" rel="noreferrer" style={{ marginLeft: 8 }}>
              {uploadedVideoLink}
            </a>
          </div>
        ) : null}
      </Modal>
    </Card>
  );
};

export default BunnyStreamSetup;
